import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Upload, X } from 'lucide-react';
import axiosInstance from '@/utils/axiosInstance';
import { API_ENDPOINTS } from '@/constants/config';

const FuelEntryForm = ({ onSuccess, onCancel, editData = null }) => {
  const [loading, setLoading] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [calculatingKmAverage, setCalculatingKmAverage] = useState(false);
  const [formData, setFormData] = useState({
    vehicle: '',
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    petrolPump: '',
    fuelType: 'petrol',
    cost: '',
    quantity: '',
    rate: '',
    odometer: '',
    kmAverage: '',
    paymentMode: 'cash',
    billPhoto: null
  });
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchVehicles();
      if (editData) {
      const editDate = new Date(editData.date);
      setFormData({
        vehicle: editData.vehicle._id || editData.vehicle,
        date: editDate.toISOString().split('T')[0],
        time: editDate.toTimeString().slice(0, 5),
        petrolPump: editData.petrolPump || '',
        fuelType: editData.fuelType || 'petrol',
        cost: editData.cost || '',
        quantity: editData.quantity,
        rate: editData.rate || '',
        odometer: editData.odometer || '',
        kmAverage: editData.kmAverage || '',
        paymentMode: editData.paymentMode,
        billPhoto: null
      });
      if (editData.billPhoto) {
        setPreview(editData.billPhoto);
      }
    }
  }, [editData]);

  const fetchVehicles = async () => {
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.vehicles);
      setVehicles(res.data.data.vehicles || []);
    } catch (err) {
      console.error("Error fetching vehicles:", err);
    }
  };

  const calculateKmAverage = async (vehicleId, currentOdometer, currentQuantity) => {
    if (!vehicleId || !currentOdometer || !currentQuantity || editData) {
      if (!vehicleId || !currentOdometer || !currentQuantity) {
        setFormData(prev => ({ ...prev, kmAverage: '' }));
      }
      return;
    }

    const currentOdometerNum = parseFloat(currentOdometer);
    const quantityNum = parseFloat(currentQuantity);

    if (isNaN(currentOdometerNum) || isNaN(quantityNum) || quantityNum <= 0) {
      setFormData(prev => ({ ...prev, kmAverage: '' }));
      return;
    }

    setCalculatingKmAverage(true);
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.fuelEntries, {
        params: {
          vehicle: vehicleId,
          limit: 1,
          page: 1
        }
      });

      const entries = res.data.data?.fuelEntries || [];
      if (entries.length > 0 && entries[0].odometer) {
        const previousOdometer = parseFloat(entries[0].odometer);

        if (currentOdometerNum > previousOdometer && quantityNum > 0) {
          const distance = currentOdometerNum - previousOdometer;
          const kmAverage = (distance / quantityNum).toFixed(2);
          setFormData(prev => ({ ...prev, kmAverage }));
        } else {
          setFormData(prev => ({ ...prev, kmAverage: '' }));
        }
      } else {
        setFormData(prev => ({ ...prev, kmAverage: '' }));
      }
    } catch (err) {
      console.error("Error calculating KM/Average:", err);
      setFormData(prev => ({ ...prev, kmAverage: '' }));
    } finally {
      setCalculatingKmAverage(false);
    }
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      
      const cost = parseFloat(updated.cost) || 0;
      const quantity = parseFloat(updated.quantity) || 0;
      const rate = parseFloat(updated.rate) || 0;

      if (name === 'cost' && cost && quantity > 0) {
        updated.rate = (cost / quantity).toFixed(2);
      } else if (name === 'quantity' && quantity > 0) {
        if (cost > 0) {
          updated.rate = (cost / quantity).toFixed(2);
        } else if (rate > 0) {
          updated.cost = (rate * quantity).toFixed(2);
        }
      } else if (name === 'rate' && rate && quantity > 0) {
        updated.cost = (rate * quantity).toFixed(2);
      }

      if (name === 'odometer' && updated.vehicle && updated.quantity) {
        calculateKmAverage(updated.vehicle, value, updated.quantity);
      }

      if (name === 'quantity' && updated.vehicle && updated.odometer) {
        calculateKmAverage(updated.vehicle, updated.odometer, value);
      }
      
      return updated;
    });
  };

  const handleVehicleChange = async (value) => {
    setFormData(prev => {
      const updated = { ...prev, vehicle: value, kmAverage: '' };
      if (value && updated.odometer && updated.quantity) {
        setTimeout(() => {
          calculateKmAverage(value, updated.odometer, updated.quantity);
        }, 100);
      }
      return updated;
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }
      setFormData(prev => ({ ...prev, billPhoto: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = () => {
    setFormData(prev => ({ ...prev, billPhoto: null }));
    setPreview(null);
  };

  const resetOdometer = () => {
    if (window.confirm('Are you sure you want to reset odometer? This will clear the current reading.')) {
      setFormData(prev => ({ ...prev, odometer: '', kmAverage: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const submitData = new FormData();
      
      const dateTime = new Date(`${formData.date}T${formData.time}`);
      
      Object.keys(formData).forEach(key => {
        if (key === 'date') {
          submitData.append('date', dateTime.toISOString());
        } else if (key === 'time') {
          return;
        } else if (key !== 'billPhoto' && formData[key] !== null && formData[key] !== '') {
          submitData.append(key, formData[key]);
        }
      });
      
      if (formData.billPhoto) {
        submitData.append('billPhoto', formData.billPhoto);
      }

      if (editData) {
        await axiosInstance.put(API_ENDPOINTS.fuelEntry(editData._id), submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await axiosInstance.post(API_ENDPOINTS.fuelEntries, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save fuel entry');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="vehicle" className="text-sm font-semibold">Vehicle *</Label>
              <Select
                value={formData.vehicle}
                onValueChange={handleVehicleChange}
                required
              >
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Select vehicle" />
                </SelectTrigger>
                <SelectContent>
                  {vehicles.map(vehicle => (
                    <SelectItem key={vehicle._id} value={vehicle._id}>
                      {vehicle.vehicleNumber} ({vehicle.type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="date" className="text-sm font-semibold">Date & Time *</Label>
              <div className="flex gap-2 mt-1.5">
                <Input
                  id="date"
                  name="date"
                  type="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                  className="flex-1"
                />
                <Input
                  id="time"
                  name="time"
                  type="time"
                  value={formData.time}
                  onChange={handleChange}
                  required
                  className="flex-1"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="fuelType" className="text-sm font-semibold">Fuel Type *</Label>
              <Select
                value={formData.fuelType}
                onValueChange={(value) => setFormData(prev => ({ ...prev, fuelType: value }))}
                required
              >
                <SelectTrigger className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="petrol">Petrol</SelectItem>
                  <SelectItem value="diesel">Diesel</SelectItem>
                  <SelectItem value="cng">CNG</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="petrolPump" className="text-sm font-semibold">Petrol Pump</Label>
              <Input
                id="petrolPump"
                name="petrolPump"
                value={formData.petrolPump}
                onChange={handleChange}
                placeholder="Enter petrol pump name"
                className="mt-1.5"
              />
            </div>

            <div>
              <Label htmlFor="cost" className="text-sm font-semibold">Cost (₹)</Label>
              <Input
                id="cost"
                name="cost"
                type="number"
                step="0.01"
                value={formData.cost}
                onChange={handleChange}
                placeholder="0.00"
                className="mt-1.5"
              />
            </div>

            <div>
              <Label htmlFor="quantity" className="text-sm font-semibold">
                Quantity ({formData.fuelType === 'cng' ? 'kg' : 'Liters'}) *
              </Label>
              <Input
                id="quantity"
                name="quantity"
                type="number"
                step="0.01"
                value={formData.quantity}
                onChange={handleChange}
                placeholder="0.00"
                required
                className="mt-1.5"
              />
            </div>

            <div>
              <Label htmlFor="rate" className="text-sm font-semibold">
                Rate per {formData.fuelType === 'cng' ? 'kg' : 'Liter'} (₹)
              </Label>
              <Input
                id="rate"
                name="rate"
                type="number"
                step="0.01"
                value={formData.rate}
                onChange={handleChange}
                placeholder="Enter rate or auto-calculated"
                className="mt-1.5"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label htmlFor="odometer" className="text-sm font-semibold">Odometer Reading</Label>
                <button
                  type="button"
                  onClick={resetOdometer}
                  className="text-xs text-blue-600 hover:text-blue-800 underline font-medium"
                >
                  Reset Odometer
                </button>
              </div>
              <Input
                id="odometer"
                name="odometer"
                type="number"
                value={formData.odometer}
                onChange={handleChange}
                placeholder="Enter odometer reading"
              />
              <p className="text-xs text-gray-500 mt-1">Click reset if you want to reset odometer</p>
            </div>

            <div>
              <Label htmlFor="kmAverage" className="text-sm font-semibold">KM/Average (km/liter)</Label>
              <Input
                id="kmAverage"
                name="kmAverage"
                type="number"
                step="0.01"
                value={formData.kmAverage}
                onChange={handleChange}
                placeholder={calculatingKmAverage ? "Calculating..." : "Auto-calculated from odometer"}
                readOnly
                className="bg-gray-50 mt-1.5"
              />
              {formData.odometer && !formData.kmAverage && !calculatingKmAverage && (
                <p className="text-xs text-gray-500 mt-1">
                  Enter odometer reading to auto-calculate KM/Average
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="paymentMode" className="text-sm font-semibold">Payment Mode *</Label>
              <Select
                value={formData.paymentMode}
                onValueChange={(value) => setFormData(prev => ({ ...prev, paymentMode: value }))}
                required
              >
                <SelectTrigger className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="billPhoto" className="text-sm font-semibold">Bill Photo</Label>
              <div className="mt-1.5">
                {preview ? (
                  <div className="relative inline-block">
                    <img
                      src={preview}
                      alt="Bill preview"
                      className="h-40 w-auto rounded-lg border-2 border-gray-200 shadow-sm"
                    />
                    <button
                      type="button"
                      onClick={removePhoto}
                      className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 shadow-md transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                    <Upload className="h-10 w-10 text-gray-400 mb-2" />
                    <span className="text-sm font-medium text-gray-600">Click to upload bill photo</span>
                    <span className="text-xs text-gray-500 mt-1">PNG, JPG up to 5MB</span>
                    <input
                      id="billPhoto"
                      name="billPhoto"
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
            </div>
          </div>

          {error && (
            <div className="p-4 bg-red-50 border-l-4 border-red-500 rounded-md">
              <p className="text-sm text-red-700 font-medium">{error}</p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={() => onCancel?.()}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="min-w-[120px]">
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                editData ? 'Update Entry' : 'Save Entry'
              )}
            </Button>
          </div>
        </form>
    </div>
  );
};

export default FuelEntryForm;
